'use client';

/**
 * SpeechRecognitionController.tsx
 *
 * Vosk.wasm-based offline voice date recognition component.
 *
 * Architecture:
 * - Uses a Web Worker (/vosk-worker.js) for all Vosk processing (keeps UI responsive)
 * - State machine: Idle → Listening → Confirming → Completed
 * - Grammar-based filter: reads grammar from sessionStorage (set by SplashScreen)
 * - Match logic: first match starts a 5.5s confirmation window; a repeat match OR
 *   timeout auto-accepts the date
 * - Silence detection: 1s of silence triggers recognizer.reset() in the worker
 * - Audio stream disposed immediately after identification to save battery
 * - Falls back to Web Speech API if Vosk worker fails to load
 *
 * Props:
 *   isActive      — controlled by parent (long press on calendar starts, any click stops)
 *   onResult      — called with the parsed Date when a date is confirmed
 *   onStop        — called when recognition ends without a result
 *   onReset       — called with a Date when a reset-date is recognized (legacy compat)
 *   maxListenMs   — max listening duration in ms (default 2 minutes)
 */

import React, { useEffect, useRef, useReducer, useCallback } from 'react';

// ── Session storage key for grammar (set by SplashScreen) ───────────────────
const GRAMMAR_SESSION_KEY = 'vosk-date-grammar';

// ── State machine types ──────────────────────────────────────────────────────

type RecognitionState = 'idle' | 'listening' | 'confirming' | 'completed';

interface RecognitionStateData {
  phase: RecognitionState;
  pendingText: string | null;
  pendingDate: Date | null;
}

type RecognitionAction =
  | { type: 'START' }
  | { type: 'MATCH'; text: string; date: Date }
  | { type: 'CONFIRM' }
  | { type: 'TIMEOUT' }
  | { type: 'STOP' }
  | { type: 'RESET' };

function recognitionReducer(
  state: RecognitionStateData,
  action: RecognitionAction
): RecognitionStateData {
  switch (action.type) {
    case 'START':
      return { phase: 'listening', pendingText: null, pendingDate: null };

    case 'MATCH':
      if (state.phase === 'listening') {
        // First match — enter confirming state
        return { phase: 'confirming', pendingText: action.text, pendingDate: action.date };
      }
      if (state.phase === 'confirming') {
        // Repeat match — confirm immediately
        return { phase: 'completed', pendingText: action.text, pendingDate: action.date };
      }
      return state;

    case 'CONFIRM': case'TIMEOUT':
      if (state.phase === 'confirming') {
        return { ...state, phase: 'completed' };
      }
      return state;

    case 'STOP': case'RESET':
      return { phase: 'idle', pendingText: null, pendingDate: null };

    default:
      return state;
  }
}

// ── Date parsing (used for both Vosk results and Web Speech fallback) ────────

const MONTH_MAP: Record<string, number> = {
  january: 0, jan: 0,
  february: 1, feb: 1,
  march: 2, mar: 2,
  april: 3, apr: 3,
  may: 4,
  june: 5, jun: 5,
  july: 6, jul: 6,
  august: 7, aug: 7,
  september: 8, sep: 8, sept: 8,
  october: 9, oct: 9,
  november: 10, nov: 10,
  december: 11, dec: 11,
};

const ORDINAL_MAP: Record<string, number> = {
  first: 1, second: 2, third: 3, fourth: 4, fifth: 5,
  sixth: 6, seventh: 7, eighth: 8, ninth: 9, tenth: 10,
  eleventh: 11, twelfth: 12, thirteenth: 13, fourteenth: 14, fifteenth: 15,
  sixteenth: 16, seventeenth: 17, eighteenth: 18, nineteenth: 19, twentieth: 20,
  'twenty-first': 21, 'twenty-second': 22, 'twenty-third': 23, 'twenty-fourth': 24,
  'twenty-fifth': 25, 'twenty-sixth': 26, 'twenty-seventh': 27, 'twenty-eighth': 28,
  'twenty-ninth': 29, thirtieth: 30, 'thirty-first': 31,
  'twenty first': 21, 'twenty second': 22, 'twenty third': 23, 'twenty fourth': 24,
  'twenty fifth': 25, 'twenty sixth': 26, 'twenty seventh': 27, 'twenty eighth': 28,
  'twenty ninth': 29, 'thirty first': 31,
  '1st': 1, '2nd': 2, '3rd': 3, '4th': 4, '5th': 5, '6th': 6, '7th': 7,
  '8th': 8, '9th': 9, '10th': 10, '11th': 11, '12th': 12, '13th': 13,
  '14th': 14, '15th': 15, '16th': 16, '17th': 17, '18th': 18, '19th': 19,
  '20th': 20, '21st': 21, '22nd': 22, '23rd': 23, '24th': 24, '25th': 25,
  '26th': 26, '27th': 27, '28th': 28, '29th': 29, '30th': 30, '31st': 31,
  one: 1, two: 2, three: 3, four: 4, five: 5,
  six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15,
  sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19, twenty: 20,
  'twenty-one': 21, 'twenty-two': 22, 'twenty-three': 23, 'twenty-four': 24,
  'twenty-five': 25, 'twenty-six': 26, 'twenty-seven': 27, 'twenty-eight': 28,
  'twenty-nine': 29, thirty: 30, 'thirty-one': 31,
};

export function parseSpokenDate(transcript: string): Date | null {
  const currentYear = new Date().getFullYear();
  const text = transcript.toLowerCase().trim().replace(/^the\s+/, '');
  let day: number | null = null;
  let month: number | null = null;

  // Pattern: "<day> [of] <month>"
  const ofPattern = /(\w+(?:[-\s]\w+)?)\s+(?:of\s+)?(\w+)/;
  const matchOf = text.match(ofPattern);
  if (matchOf) {
    const [, dayPart, monthPart] = matchOf;
    const parsedDay = parseInt(dayPart) || ORDINAL_MAP[dayPart];
    const parsedMonth = MONTH_MAP[monthPart];
    if (parsedDay && parsedMonth !== undefined) {
      day = parsedDay;
      month = parsedMonth;
    }
  }

  // Pattern: "<month> <day>"
  if (day === null) {
    const monthFirstPattern = /(\w+)\s+(\w+(?:[-\s]\w+)?)/;
    const matchMF = text.match(monthFirstPattern);
    if (matchMF) {
      const [, monthPart, dayPart] = matchMF;
      const parsedMonth = MONTH_MAP[monthPart];
      const parsedDay = parseInt(dayPart) || ORDINAL_MAP[dayPart];
      if (parsedMonth !== undefined && parsedDay && parsedDay > 0 && parsedDay <= 31) {
        month = parsedMonth;
        day = parsedDay;
      }
    }
  }

  // Pattern: numeric "DD/MM" or "MM/DD"
  if (day === null) {
    const numPattern = /(\d{1,2})[\/\-\.](\d{1,2})/;
    const matchNum = text.match(numPattern);
    if (matchNum) {
      const [, a, b] = matchNum;
      const na = parseInt(a), nb = parseInt(b);
      if (na >= 1 && na <= 31 && nb >= 1 && nb <= 12) {
        day = na; month = nb - 1;
      }
    }
  }

  if (day !== null && month !== null) {
    const result = new Date(currentYear, month, day);
    if (!isNaN(result.getTime())) return result;
  }

  return null;
}

// ── Web Speech API fallback types ────────────────────────────────────────────

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognitionInstance;
    webkitSpeechRecognition: new () => SpeechRecognitionInstance;
  }
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
}
interface SpeechRecognitionEvent { results: SpeechRecognitionResultList; }
interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionAlternative { transcript: string; confidence: number; }
interface SpeechRecognitionErrorEvent extends Event { error: string; }

// ── Component props ──────────────────────────────────────────────────────────

interface Props {
  isActive: boolean;
  onResult: (date: Date) => void;
  onStop: () => void;
  onReset: (date: Date) => void;
  maxListenMs?: number;
}

const CONFIRMATION_WINDOW_MS = 5500;
const MAX_LISTEN_MS = 2 * 60 * 1000;

// ── Main component ───────────────────────────────────────────────────────────

export default function SpeechRecognitionController({
  isActive,
  onResult,
  onStop,
  maxListenMs,
}: Props) {
  const [recState, dispatch] = useReducer(recognitionReducer, {
    phase: 'idle',
    pendingText: null,
    pendingDate: null,
  });

  // Stable refs for callbacks
  const onResultRef = useRef(onResult);
  const onStopRef = useRef(onStop);
  useEffect(() => { onResultRef.current = onResult; }, [onResult]);
  useEffect(() => { onStopRef.current = onStop; }, [onStop]);

  // Worker and audio refs
  const workerRef = useRef<Worker | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxListenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recStateRef = useRef(recState);
  const isActiveRef = useRef(isActive);

  // Web Speech fallback refs
  const speechRecRef = useRef<SpeechRecognitionInstance | null>(null);
  const usingFallbackRef = useRef(false);
  const fallbackResolvedRef = useRef(false);

  useEffect(() => { recStateRef.current = recState; }, [recState]);
  useEffect(() => { isActiveRef.current = isActive; }, [isActive]);

  // ── Cleanup function ─────────────────────────────────────────────────────

  const cleanup = useCallback(() => {
    // Clear timers
    if (confirmTimerRef.current) {
      clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = null;
    }
    if (maxListenTimerRef.current) {
      clearTimeout(maxListenTimerRef.current);
      maxListenTimerRef.current = null;
    }

    // Stop Web Speech fallback
    if (speechRecRef.current) {
      fallbackResolvedRef.current = true;
      try { speechRecRef.current.abort(); } catch { /* ignore */ }
      speechRecRef.current = null;
    }

    // Disconnect audio processor
    if (processorRef.current) {
      try { processorRef.current.disconnect(); } catch { /* ignore */ }
      processorRef.current = null;
    }
    if (sourceRef.current) {
      try { sourceRef.current.disconnect(); } catch { /* ignore */ }
      sourceRef.current = null;
    }

    // Stop media stream tracks immediately (saves battery)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    // Suspend AudioContext
    if (audioContextRef.current) {
      try { audioContextRef.current.suspend(); } catch { /* ignore */ }
      audioContextRef.current = null;
    }

    // Stop worker
    if (workerRef.current) {
      try { workerRef.current.postMessage({ type: 'stop' }); } catch { /* ignore */ }
      workerRef.current.terminate();
      workerRef.current = null;
    }
  }, []);

  // ── Accept a confirmed date ──────────────────────────────────────────────

  const acceptDate = useCallback((date: Date) => {
    dispatch({ type: 'TIMEOUT' }); // move to completed
    cleanup();
    onResultRef.current(date);
  }, [cleanup]);

  // ── Start confirmation timer ─────────────────────────────────────────────

  const startConfirmTimer = useCallback((date: Date) => {
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    confirmTimerRef.current = setTimeout(() => {
      confirmTimerRef.current = null;
      // Auto-accept after 5.5s window
      acceptDate(date);
    }, CONFIRMATION_WINDOW_MS);
  }, [acceptDate]);

  // ── Handle a text match from the worker ─────────────────────────────────

  const handleMatch = useCallback((text: string) => {
    const date = parseSpokenDate(text);
    if (!date) return;

    const currentPhase = recStateRef.current.phase;

    if (currentPhase === 'listening') {
      dispatch({ type: 'MATCH', text, date });
      startConfirmTimer(date);
    } else if (currentPhase === 'confirming') {
      // Repeat match — confirm immediately
      if (confirmTimerRef.current) {
        clearTimeout(confirmTimerRef.current);
        confirmTimerRef.current = null;
      }
      dispatch({ type: 'MATCH', text, date });
      acceptDate(date);
    }
  }, [startConfirmTimer, acceptDate]);

  // ── Start Vosk worker recognition ────────────────────────────────────────

  const startVoskRecognition = useCallback(async () => {
    const grammar = (typeof window !== 'undefined'
      ? sessionStorage.getItem(GRAMMAR_SESSION_KEY)
      : null) || null;

    // Create worker
    let worker: Worker;
    try {
      worker = new Worker('/vosk-worker.js');
      workerRef.current = worker;
    } catch (e) {
      return false; // Worker creation failed
    }

    let workerReady = false;

    worker.onmessage = (e) => {
      if (!isActiveRef.current) return;
      const { type, text } = e.data;

      if (type === 'ready') {
        workerReady = true;
      } else if (type === 'match') {
        handleMatch(text);
      } else if (type === 'silence') {
        // Worker detected 1s silence — already reset recognizer
      } else if (type === 'error') {
        console.warn('[VoskWorker]', e.data.message);
      }
    };

    worker.onerror = () => {
      // Worker crashed — fall back to Web Speech
      return false;
    };

    // Send start command
    worker.postMessage({ type: 'start', grammar });

    // Wait for worker ready (up to 30s for model download)
    await new Promise<void>((resolve) => {
      const checkReady = setInterval(() => {
        if (workerReady || !isActiveRef.current) {
          clearInterval(checkReady);
          resolve();
        }
      }, 100);
      setTimeout(() => { clearInterval(checkReady); resolve(); }, 30000);
    });

    if (!isActiveRef.current) return false;

    // Request microphone
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        }
      });
    } catch (e) {
      return false;
    }

    if (!isActiveRef.current) {
      stream.getTracks().forEach(t => t.stop());
      return false;
    }

    streamRef.current = stream;

    // Create AudioContext and ScriptProcessor to feed audio to worker
    const AudioContextClass = (window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
    const ctx = new AudioContextClass({ sampleRate: 16000 });
    audioContextRef.current = ctx;

    const source = ctx.createMediaStreamSource(stream);
    sourceRef.current = source;

    // ScriptProcessorNode (deprecated but widely supported in PWAs)
    const processor = ctx.createScriptProcessor(4096, 1, 1);
    processorRef.current = processor;

    processor.onaudioprocess = (e) => {
      if (!isActiveRef.current || !workerRef.current) return;
      const inputData = e.inputBuffer.getChannelData(0);
      const buffer = inputData.buffer.slice(
        inputData.byteOffset,
        inputData.byteOffset + inputData.byteLength
      );
      workerRef.current.postMessage({ type: 'audio', buffer }, [buffer]);
    };

    source.connect(processor);
    processor.connect(ctx.destination);

    return true;
  }, [handleMatch]);

  // ── Web Speech API fallback ──────────────────────────────────────────────

  const startWebSpeechFallback = useCallback(() => {
    const SpeechRecognitionAPI =
      typeof window !== 'undefined'
        ? (window.SpeechRecognition || window.webkitSpeechRecognition)
        : null;

    if (!SpeechRecognitionAPI) {
      onStopRef.current();
      return;
    }

    fallbackResolvedRef.current = false;
    usingFallbackRef.current = true;

    function startRec() {
      if (fallbackResolvedRef.current || !isActiveRef.current) return;

      const recognition = new SpeechRecognitionAPI!();
      speechRecRef.current = recognition;
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        if (fallbackResolvedRef.current) return;
        for (let i = 0; i < event.results.length; i++) {
          const result = event.results[i];
          for (let j = 0; j < result.length; j++) {
            const transcript = result[j]?.transcript ?? '';
            handleMatch(transcript);
          }
        }
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        if (fallbackResolvedRef.current) return;
        if (event.error === 'aborted') return;
        speechRecRef.current = null;
        setTimeout(() => startRec(), 200);
      };

      recognition.onend = () => {
        if (fallbackResolvedRef.current) return;
        speechRecRef.current = null;
        setTimeout(() => startRec(), 200);
      };

      try { recognition.start(); } catch { /* ignore */ }
    }

    startRec();
  }, [handleMatch]);

  // ── Main effect: start/stop recognition based on isActive ───────────────

  useEffect(() => {
    if (!isActive) {
      cleanup();
      dispatch({ type: 'STOP' });
      return;
    }

    dispatch({ type: 'START' });

    // Max listen timeout
    maxListenTimerRef.current = setTimeout(() => {
      cleanup();
      dispatch({ type: 'STOP' });
      onStopRef.current();
    }, maxListenMs ?? MAX_LISTEN_MS);

    // Try Vosk worker first, fall back to Web Speech API
    startVoskRecognition().then((voskStarted) => {
      if (!isActiveRef.current) return;
      if (!voskStarted) {
        // Vosk unavailable — use Web Speech API
        startWebSpeechFallback();
      }
    });

    // Cleanup on unmount or when isActive becomes false
    return () => {
      cleanup();
      dispatch({ type: 'STOP' });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // ── Handle page visibility change (kill switch) ──────────────────────────

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && isActiveRef.current) {
        cleanup();
        dispatch({ type: 'STOP' });
        onStopRef.current();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [cleanup]);

  return null;
}